/* eslint-env browser */
import { UIManager, GameContextManager, MCPBridgeManager, } from '@balancy/ui-core';
import { SDKAdapter } from './adapters/SDKAdapter';
import { ContextCache } from './cache/ContextCache';
class BalancyAISDK {
    constructor() {
        this.adapter = null;
        this.uiManager = null;
        this.gameContextManager = null;
        this.contextCache = new ContextCache();
        this.initialized = false;
        this.assetsBaseUrl = './dist';
        this.healthCheckInterval = null;
        this.lastConnectionStatus = null;
        this.analyticsPollingTimer = null;
        this.analyticsHostObserver = null;
        this.toolHandlers = new Map();
        this.toolCatalog = [];
        this.entitiesSavedHandler = null;
        this.mcpBridge = null;
        this.lastMcpStatus = null;
        this.reclaimInFlight = false;
        this.apiBaseUrl = '';
        this.onUserMessage = null;
        this.state = {
            user: null,
            games: {},
            currentGameId: null,
            currentBranchName: null,
            fullUrl: null,
            simplifiedUrl: null,
            pageObjectId: null,
        };
    }
    /**
     * Step 1: Initialize the Balancy AI SDK
     * Sets up URL monitoring and prepares the SDK for use
     * @param options - Optional configuration (apiBaseUrl, assetsBaseUrl)
     */
    init(options) {
        if (this.initialized) {
            console.warn('[BalancyAI] Already initialized');
            return;
        }
        const apiBaseUrl = options?.apiBaseUrl || 'https://balancy-ai.balancy.cloud';
        const assetsBaseUrl = options?.assetsBaseUrl || './dist';
        this.assetsBaseUrl = assetsBaseUrl;
        this.apiBaseUrl = apiBaseUrl;
        this.onUserMessage = options?.onUserMessage || null;
        console.log(`[BalancyAI] Initializing SDK v${__BALANCY_SDK_VERSION__} with API:`, apiBaseUrl);
        // Create game context manager
        this.gameContextManager = new GameContextManager();
        // Parse initial URL
        this.updateURLContext(window.location.href);
        // Create adapter (will be fully configured after authenticate + setGameContext)
        this.adapter = new SDKAdapter({
            apiBaseUrl,
            assetsBaseUrl,
            initialContext: this.gameContextManager.getCurrentContext(),
            user: undefined,
            accessToken: undefined,
            toolExecutor: (toolName, args) => this.executeToolLocally(toolName, args),
            confirmationHandler: (toolName, description, args) => this.showToolConfirmation(toolName, description, args),
            contextCache: this.contextCache,
        });
        // Wire the user-message analytics callback
        if (this.onUserMessage) {
            this.adapter.setUserMessageCallback(this.onUserMessage);
        }
        // Wire cache to use executeToolLocally for refresh/restore
        this.contextCache.setToolExecutor((toolName, args) => this.executeToolLocally(toolName, args));
        // Listen for entity ID remaps after save/deploy
        this.entitiesSavedHandler = (e) => {
            const detail = e.detail;
            if (detail?.idMapping && typeof detail.idMapping === 'object') {
                console.log('[BalancyAI] Entities saved, remapping IDs:', detail.idMapping);
                this.adapter?.notifyEntitiesSaved(detail.idMapping);
            }
        };
        window.addEventListener('balancy-entities-saved', this.entitiesSavedHandler);
        // Create UI manager
        this.uiManager = new UIManager(this.adapter);
        this.uiManager.setVersion(__BALANCY_SDK_VERSION__);
        this.uiManager.setAssetsBaseUrl(this.assetsBaseUrl);
        // Wire execution plan progress events to UI
        this.adapter.onProgress((event) => {
            this.uiManager?.handleProgressEvent(event);
        });
        // Wire MCP bridge handlers
        this.uiManager.setMCPHandlers(() => this.connectExternalAI(), () => this.disconnectExternalAI());
        // Safe tab reclaim: when a 'replaced' tab becomes visible and no other tab
        // is holding the bridge (the holder was closed), take it back automatically.
        // Gated on a server check so two visible tabs never ping-pong the bridge.
        const onTabVisible = () => {
            if (typeof document !== 'undefined' &&
                document.visibilityState !== 'visible') {
                return;
            }
            void this.maybeReclaimBridge();
        };
        document.addEventListener('visibilitychange', onTabVisible);
        window.addEventListener('focus', onTabVisible);
        // Initialize UI
        this.uiManager.createFloatingIcon();
        this.uiManager.createChatPanel(null);
        // Start monitoring URL changes
        this.startURLMonitoring();
        // Run initial health check and start periodic monitoring
        this.checkConnection();
        this.healthCheckInterval = setInterval(() => this.checkConnection(), 30000);
        this.initialized = true;
        console.log('[BalancyAI] SDK initialized successfully');
    }
    /**
     * Check server connection and update UI status
     */
    async checkConnection() {
        if (!this.adapter || !this.uiManager)
            return;
        // Don't flash 'connecting' on every 30s poll — that spammed the console and
        // flickered the avatar online/offline even though nothing changed. Only push
        // a status when it actually transitions.
        const isConnected = await this.adapter.healthCheck();
        const newStatus = isConnected ? 'connected' : 'disconnected';
        if (newStatus !== this.lastConnectionStatus) {
            this.lastConnectionStatus = newStatus;
            this.uiManager.setConnectionStatus(newStatus);
        }
    }
    /**
     * Step 2: Authenticate user
     * Must be called before any API operations
     */
    async authenticate(params) {
        if (!this.initialized) {
            throw new Error('[BalancyAI] SDK not initialized. Call init() first.');
        }
        console.log('[BalancyAI] Authenticating user:', {
            email: params.email,
            userId: params.userId,
            hasToken: !!params.token,
            tokenLength: params.token?.length ?? 0,
        });
        // Store user in state
        this.state.user = params;
        // Update adapter with user info
        const user = {
            id: params.userId,
            name: params.name,
            email: params.email,
        };
        this.adapter?.setAuth(user, params.token);
        // Exchange the Balancy S2S token for an internal JWT so
        // all subsequent requests use Authorization: Bearer <jwt>
        // instead of per-request S2S validation.
        const exchangeResult = await this.adapter?.exchangeBalancyToken(params.email, params.token);
        if (exchangeResult && !exchangeResult.success) {
            console.error('[BalancyAI] Token exchange failed:', exchangeResult.error);
            // Show auth error UI with retry
            this.uiManager?.showAuthError(exchangeResult.reason === 'token_invalid'
                ? 'Your session token is invalid or expired. Please refresh the page.'
                : 'Could not connect to the AI server. Please check your connection and try again.', () => this.authenticate(params));
            return;
        }
        // Update user context in adapter
        this.updateAdapterContext();
        // Check if the user needs to accept terms before proceeding
        if (exchangeResult?.success && exchangeResult.needsTermsAcceptance) {
            console.log('[BalancyAI] User needs to accept terms, showing welcome screen');
            this.uiManager?.showTermsAcceptance(user, async () => {
                // Fire-and-forget: persist acceptance on server
                this.adapter?.acceptTerms();
                // Transition to the normal chat panel
                try {
                    await this.uiManager?.updatePanelWithUser(user);
                }
                catch (error) {
                    console.error('[BalancyAI] Failed to update panel after terms acceptance:', error);
                }
            });
        }
        else {
            // Await panel recreation so the new authenticated panel is ready
            try {
                await this.uiManager?.updatePanelWithUser(user);
            }
            catch (error) {
                console.error('[BalancyAI] Failed to update panel after auth:', error);
            }
        }
        console.log('[BalancyAI] User authenticated successfully');
        // If already on the analytics page, inject now that we have auth
        const currentContext = this.gameContextManager?.getCurrentContext();
        if (currentContext) {
            this.handleAnalyticsPageInjection(currentContext);
        }
        // A rotated token has to reach an already-built bridge: tryAutoConnect is
        // gated on there being no bridge, so on re-auth it does nothing and the
        // bridge would keep dialing with the dead token it captured at construction.
        this.mcpBridge?.updateAuthToken(params.token);
        // Auto-connect MCP bridge if game context is already set
        this.tryAutoConnect();
    }
    /**
     * Step 3: Set game context
     * Can be called multiple times for different games in the same session
     */
    setGameContext(params) {
        if (!this.initialized) {
            throw new Error('[BalancyAI] SDK not initialized. Call init() first.');
        }
        if (!this.state.user) {
            throw new Error('[BalancyAI] User not authenticated. Call authenticate() first.');
        }
        console.log('[BalancyAI] Setting game context:', params);
        // Store game context in state
        this.state.games[params.apiGameId] = {
            publicKey: params.publicKey,
        };
        // Set as current game
        this.state.currentGameId = params.apiGameId;
        // Update adapter context (userContextData for chat messages)
        this.updateAdapterContext();
        // Also update the adapter's GameContext so getContext() returns the gameId.
        // This is needed for features like credits that rely on getContext().apiGameId.
        if (this.adapter) {
            const existingContext = this.adapter.getContext();
            this.adapter.updateContext({
                ...(existingContext || {}),
                apiGameId: params.apiGameId,
                publicKey: params.publicKey,
                isGamePage: true,
            });
        }
        console.log('[BalancyAI] Game context set successfully. Total games:', Object.keys(this.state.games).length);
        // If already on the analytics page, inject now that game context is available
        const currentContext = this.gameContextManager?.getCurrentContext();
        if (currentContext) {
            this.handleAnalyticsPageInjection(currentContext);
        }
        // Auto-connect MCP bridge now that game context is available
        this.tryAutoConnect();
    }
    /**
     * Step 4: Register client-side tools
     *
     * Allows the host application (e.g. Balancy Admin Panel) to register
     * tool handlers that the LLM can invoke. The handler functions execute
     * locally in the browser — only descriptions and parameter schemas
     * are sent to the backend.
     *
     * Calling registerTools() again replaces the entire tool set.
     *
     * @param tools - Map of tool name → { description, parameters, handler }
     */
    registerTools(tools, options = {}) {
        if (!this.initialized) {
            throw new Error('[BalancyAI] SDK not initialized. Call init() first.');
        }
        if (!this.state.user) {
            throw new Error('[BalancyAI] User not authenticated. Call authenticate() first.');
        }
        // Clear previous registrations
        this.toolHandlers.clear();
        this.toolCatalog = [];
        // Store handlers locally, extract catalog for backend
        for (const [name, definition] of Object.entries(tools)) {
            this.toolHandlers.set(name, definition.handler);
            this.toolCatalog.push({
                name,
                description: definition.description,
                parameters: definition.parameters,
            });
        }
        console.log(`[BalancyAI] Registered ${this.toolCatalog.length} client-side tools:`, this.toolCatalog.map((t) => t.name));
        // Send catalog to backend (via adapter)
        this.adapter?.syncToolCatalog(this.toolCatalog, options.contractVersion);
    }
    /**
     * Set the ID of the object the user is currently viewing.
     *
     * The host application (e.g. Balancy Admin Panel) should call this whenever
     * the user opens or navigates to a specific object (view, entity, etc.).
     * The ID is included in the user context so the LLM knows which object to target.
     *
     * Call with `null` when the user leaves the object page.
     *
     * @param unnyId - The unnyId (or other meaningful ID) of the currently viewed object, or null
     *
     * @example
     * // When user opens a view in the UI Builder:
     * BalancyAI.setCurrentPageObject('40171');
     *
     * // When user navigates away from the view:
     * BalancyAI.setCurrentPageObject(null);
     */
    setCurrentPageObject(unnyId) {
        this.state.pageObjectId = unnyId;
        console.log('[BalancyAI] Current page object set:', unnyId);
        this.updateAdapterContext();
    }
    /**
     * Execute a tool locally by name.
     * Called by the adapter when the backend requests a client-side tool invocation.
     *
     * @param toolName - The registered tool name
     * @param args - The arguments from the LLM
     * @returns The tool result (JSON-serializable)
     */
    async executeToolLocally(toolName, args) {
        const handler = this.toolHandlers.get(toolName);
        if (!handler) {
            return {
                success: false,
                error: `Unknown client-side tool: ${toolName}`,
            };
        }
        try {
            const result = await handler(args);
            return { success: true, result };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`[BalancyAI] Client-side tool "${toolName}" failed:`, error);
            return { success: false, error: message };
        }
    }
    // -------------------------------------------------------------------------
    // External AI (MCP Bridge)
    // -------------------------------------------------------------------------
    /**
     * Automatically connect MCP bridge when user is authenticated and a game is selected.
     */
    tryAutoConnect() {
        if (this.state.user && this.state.currentGameId && !this.mcpBridge) {
            console.log('[BalancyAI] Auto-connecting MCP bridge...');
            this.connectExternalAI();
        }
    }
    /**
     * If this tab's bridge was evicted by another tab ('replaced'), reclaim it —
     * but only when the server confirms no other tab is holding it (the holder
     * closed). This is the auto half of the WhatsApp-Web reclaim model; the manual
     * half is the "Use here" button, which always takes over.
     */
    async maybeReclaimBridge() {
        if (this.lastMcpStatus !== 'replaced' || this.reclaimInFlight)
            return;
        if (!this.state.user)
            return;
        this.reclaimInFlight = true;
        try {
            const response = await fetch(`${this.apiBaseUrl}/api/mcp/bridge-status`, {
                headers: {
                    'X-Balancy-Email': this.state.user.email,
                    'X-Balancy-Token': this.state.user.token,
                },
            });
            if (!response.ok)
                return; // Can't confirm — don't risk stealing a live tab.
            const data = (await response.json());
            if (data.connected === false) {
                console.log('[BalancyAI] Bridge is free — reclaiming for this tab');
                this.connectExternalAI();
            }
        }
        catch {
            // Network error — leave the bridge where it is.
        }
        finally {
            this.reclaimInFlight = false;
        }
    }
    /**
     * Connect the browser to the MCP bridge for external AI tool execution.
     */
    connectExternalAI() {
        if (!this.state.user) {
            console.warn('[BalancyAI] Cannot connect MCP bridge — not authenticated');
            this.uiManager?.setMCPError('Please log in first to connect the AI bridge.');
            return;
        }
        if (!this.state.currentGameId) {
            console.warn('[BalancyAI] Cannot connect MCP bridge — no game selected');
            this.uiManager?.setMCPError('Please select a game first (open a game page in Balancy).');
            return;
        }
        // Disconnect existing bridge if any
        if (this.mcpBridge) {
            this.mcpBridge.disconnect();
        }
        this.mcpBridge = new MCPBridgeManager({
            serverUrl: this.apiBaseUrl,
            email: this.state.user.email,
            authToken: this.state.user.token,
            gameId: this.state.currentGameId,
            // No 'dev' fallback: a guessed branch reads as a real one to the LLM.
            branchName: this.getBridgeBranchName(),
            simplifiedUrl: this.state.simplifiedUrl,
            pageObjectId: this.state.pageObjectId,
            toolExecutor: (toolName, args) => this.executeToolLocally(toolName, args),
            onStatusChange: (status) => {
                this.lastMcpStatus = status;
                this.uiManager?.setMCPStatus(status);
            },
            onActivity: (activity) => {
                this.uiManager?.addMCPActivity(activity);
            },
        });
        this.mcpBridge.connect();
    }
    /**
     * Disconnect from the MCP bridge.
     */
    disconnectExternalAI() {
        if (this.mcpBridge) {
            this.mcpBridge.disconnect();
            this.mcpBridge = null;
        }
    }
    /**
     * Show a tool confirmation prompt inline in the chat panel.
     * Called by the adapter when the backend requires user approval for a dangerous tool.
     */
    showToolConfirmation(toolName, description, args) {
        if (!this.uiManager) {
            console.warn('[BalancyAI] No UI manager — auto-rejecting confirmation');
            return Promise.resolve({ approved: false, comment: 'No UI available' });
        }
        return this.uiManager.showToolConfirmation(toolName, description, args);
    }
    /**
     * Get registered tool names (for debugging)
     */
    getRegisteredTools() {
        return Array.from(this.toolHandlers.keys());
    }
    /**
     * Update adapter with current user context data
     */
    updateAdapterContext() {
        if (!this.adapter)
            return;
        const publicKey = this.state.currentGameId
            ? this.state.games[this.state.currentGameId]?.publicKey
            : null;
        this.adapter.setUserContextData({
            name: this.state.user?.name || null,
            apiGameId: this.state.currentGameId,
            publicKey: publicKey || null,
            url: this.state.fullUrl,
            simplifiedUrl: this.state.simplifiedUrl,
            pageObjectId: this.state.pageObjectId,
        });
        // Game and branch go along with the page: bridge_init only carries them
        // once, when the bridge connects.
        if (this.mcpBridge) {
            this.mcpBridge.updateContext({
                gameId: this.state.currentGameId,
                branchName: this.getBridgeBranchName(),
                simplifiedUrl: this.state.simplifiedUrl,
                pageObjectId: this.state.pageObjectId,
            });
        }
    }
    /**
     * The current branch name as the bridge reports it. The Admin Panel writes
     * "/" in branch names as ":" in the URL, so turn it back.
     */
    getBridgeBranchName() {
        return this.state.currentBranchName?.replace(/:/g, '/') ?? null;
    }
    /**
     * Get current SDK state (for debugging)
     */
    getState() {
        return { ...this.state };
    }
    /**
     * Start monitoring URL changes to update context automatically
     */
    startURLMonitoring() {
        // Monitor browser navigation (back/forward buttons)
        window.addEventListener('popstate', () => {
            this.handleURLChange();
        });
        // Monitor pushState/replaceState (programmatic navigation)
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        history.pushState = (...args) => {
            originalPushState.apply(history, args);
            this.handleURLChange();
        };
        history.replaceState = (...args) => {
            originalReplaceState.apply(history, args);
            this.handleURLChange();
        };
        console.log('[BalancyAI] URL monitoring started');
    }
    /**
     * Handle URL changes and update context
     */
    handleURLChange() {
        if (!this.gameContextManager || !this.adapter)
            return;
        this.updateURLContext(window.location.href);
        const newContext = this.gameContextManager.getCurrentContext();
        console.log('[BalancyAI] URL changed, new context:', newContext);
        this.adapter.updateContext(newContext);
        // Validate game context if needed
        if (this.state.currentGameId &&
            !this.state.games[this.state.currentGameId]) {
            console.warn(`[BalancyAI] Game context not set for apiGameId: ${this.state.currentGameId}. Call setGameContext() first.`);
        }
        // Check sync status if on game page
        if (newContext.isGamePage &&
            newContext.apiGameId &&
            newContext.branchName) {
            setTimeout(async () => {
                const status = await this.adapter.checkGameSyncStatus(newContext.apiGameId, newContext.branchName);
                this.uiManager.updateGameSyncStatus(status);
            }, 500);
        }
        // Auto-inject analytics when navigating to the analytics page
        this.handleAnalyticsPageInjection(newContext);
    }
    /**
     * Detect the /analytics page and inject the dashboard into the host-provided div.
     * Unmounts when navigating away.
     * Polls for the target element since the host SPA may render it asynchronously.
     */
    handleAnalyticsPageInjection(context) {
        if (!this.uiManager)
            return;
        // Don't mount analytics until the user is authenticated — the dashboard
        // needs auth credentials (token / email) for API calls.  The injection
        // will be re-triggered by authenticate() and setGameContext() once ready.
        if (!this.state.user)
            return;
        // Cancel any in-flight polling from a previous call
        if (this.analyticsPollingTimer) {
            clearTimeout(this.analyticsPollingTimer);
            this.analyticsPollingTimer = null;
        }
        // Stop watching for host element removal
        this.stopAnalyticsWatch();
        const isAnalyticsPage = context.isGamePage &&
            context.section === 'analytics' &&
            context.apiGameId;
        if (isAnalyticsPage) {
            const gameId = context.apiGameId;
            let attempts = 0;
            const maxAttempts = 30; // 30 × 300ms = 9 seconds max
            const tryInject = async () => {
                const host = document.getElementById('balancy-analytics-root');
                if (host) {
                    this.analyticsPollingTimer = null;
                    console.log('[BalancyAI] Analytics page detected, injecting dashboard into', host);
                    // Await the mount so we don't set up the watcher while the
                    // async mount is still running (the host element could be
                    // replaced by the SPA framework during the await, making the
                    // captured reference stale).
                    await this.uiManager.mountAnalyticsInline(host, this.assetsBaseUrl, gameId);
                    // The host SPA may have re-rendered during the async mount,
                    // replacing the DOM node we mounted into. If so, the current
                    // #balancy-analytics-root is a new element and our mount went
                    // into a detached node. Re-mount into the live element.
                    const currentHost = document.getElementById('balancy-analytics-root');
                    if (currentHost && currentHost !== host) {
                        console.log('[BalancyAI] Host element was replaced during mount, re-injecting into new element');
                        this.uiManager.unmountAnalyticsInline();
                        await this.uiManager.mountAnalyticsInline(currentHost, this.assetsBaseUrl, gameId);
                    }
                    // Watch for host element being replaced by the SPA framework
                    // (happens on fresh page load when React re-renders after hydration)
                    this.watchAnalyticsHost(gameId);
                }
                else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        this.analyticsPollingTimer = setTimeout(tryInject, 300);
                    }
                    else {
                        console.warn('[BalancyAI] Analytics page detected but #balancy-analytics-root not found after polling');
                        this.analyticsPollingTimer = null;
                    }
                }
            };
            // Start polling — first attempt after a short delay
            console.log('[BalancyAI] Analytics page detected, polling for #balancy-analytics-root...');
            this.analyticsPollingTimer = setTimeout(tryInject, 100);
        }
        else {
            // Navigated away from analytics — unmount if active
            this.uiManager.unmountAnalyticsInline();
        }
    }
    /**
     * Stop the analytics host MutationObserver.
     */
    stopAnalyticsWatch() {
        if (this.analyticsHostObserver) {
            this.analyticsHostObserver.disconnect();
            this.analyticsHostObserver = null;
        }
    }
    /**
     * Watch for the host element (#balancy-analytics-root) being replaced by the
     * SPA framework. On fresh page loads, React may re-render the entire tree
     * after our initial injection, wiping our dashboard. When that happens,
     * re-inject into the new element.
     *
     * Observes document.body (which is never replaced) instead of the host's
     * parent, so the observer stays connected even when React replaces the
     * entire subtree above the host element.
     */
    watchAnalyticsHost(gameId) {
        this.stopAnalyticsWatch();
        let reinjectQueued = false;
        this.analyticsHostObserver = new MutationObserver(() => {
            if (reinjectQueued)
                return;
            const host = document.getElementById('balancy-analytics-root');
            if (!host)
                return; // element removed entirely — will be re-detected by URL change
            // If the host element exists but our dashboard is gone, re-inject
            if (!host.querySelector('.balancy-analytics')) {
                reinjectQueued = true;
                console.log('[BalancyAI] Analytics host was replaced by SPA, re-injecting...');
                this.uiManager?.unmountAnalyticsInline();
                this.uiManager
                    ?.mountAnalyticsInline(host, this.assetsBaseUrl, gameId)
                    .then(() => {
                    reinjectQueued = false;
                })
                    .catch(() => {
                    reinjectQueued = false;
                });
            }
        });
        this.analyticsHostObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
    /**
     * Update URL context and extract fullUrl and simplifiedUrl
     */
    updateURLContext(url) {
        if (!this.gameContextManager)
            return;
        const context = this.gameContextManager.parseURL(url);
        // Update state with current game and branch
        this.state.currentGameId = context.apiGameId;
        this.state.currentBranchName = context.branchName;
        // When navigating away from an object page, clear the pageObjectId.
        // When navigating TO an object page, we don't set pageObjectId from the URL
        // because the URL contains a GUID (not a usable unnyId).
        // The host app should call setCurrentPageObject(unnyId) explicitly.
        if (!context.entityId) {
            this.state.pageObjectId = null;
        }
        // Extract fullUrl (pathname excluding domain)
        try {
            const urlObj = new URL(url);
            this.state.fullUrl = urlObj.pathname;
            // Extract simplifiedUrl (section/subsection pattern)
            if (context.isGamePage && context.section) {
                // Pattern: /dashboard/games/{apiGameId}/{branchName}/section/subsection/...
                // We want: "Section/Subsection"
                const section = this.formatSectionName(context.section);
                const subsection = context.subsection
                    ? this.formatSectionName(context.subsection)
                    : null;
                this.state.simplifiedUrl = subsection
                    ? `${section}/${subsection}`
                    : section;
            }
            else {
                this.state.simplifiedUrl = null;
            }
            console.log('[BalancyAI] URL context updated:', {
                fullUrl: this.state.fullUrl,
                simplifiedUrl: this.state.simplifiedUrl,
                pageObjectId: this.state.pageObjectId,
                apiGameId: this.state.currentGameId,
                branchName: this.state.currentBranchName,
            });
            // Update adapter with new context
            this.updateAdapterContext();
        }
        catch (error) {
            console.error('[BalancyAI] Failed to parse URL:', error);
        }
    }
    formatSectionName(slug) {
        return (BalancyAISDK.SECTION_DISPLAY_NAMES[slug.toLowerCase()] ||
            this.capitalize(slug));
    }
    capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
    /**
     * @deprecated Use authenticate() instead
     */
    setUser(userId, userEmail, token) {
        console.warn('[BalancyAI] setUser() is deprecated, use authenticate() instead');
        this.authenticate({
            email: userEmail,
            name: userEmail.split('@')[0],
            userId,
            token: token || '',
        });
    }
    /**
     * @deprecated Use authenticate() instead
     */
    setAuth(user, accessToken) {
        console.warn('[BalancyAI] setAuth() is deprecated, use authenticate() instead');
        this.authenticate({
            email: user.email,
            name: user.name,
            userId: user.id,
            token: accessToken,
        });
    }
    /**
     * Logout current user
     */
    logout() {
        if (!this.adapter || !this.uiManager) {
            console.error('[BalancyAI] SDK not initialized');
            return;
        }
        this.adapter.logout();
        this.uiManager.removePanel();
        this.uiManager.createChatPanel(null);
    }
    /**
     * Show the analytics dashboard overlay.
     * Lazily loads the analytics IIFE bundle from the CDN.
     */
    async showAnalytics() {
        if (!this.uiManager) {
            console.error('[BalancyAI] SDK not initialized');
            return;
        }
        await this.uiManager.showAnalytics(this.assetsBaseUrl);
    }
    /**
     * Hide the analytics dashboard overlay.
     */
    hideAnalytics() {
        if (!this.uiManager)
            return;
        this.uiManager.hideAnalytics();
    }
    /**
     * Destroy the SDK (cleanup)
     */
    destroy() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
        if (this.analyticsPollingTimer) {
            clearTimeout(this.analyticsPollingTimer);
            this.analyticsPollingTimer = null;
        }
        this.stopAnalyticsWatch();
        if (this.entitiesSavedHandler) {
            window.removeEventListener('balancy-entities-saved', this.entitiesSavedHandler);
            this.entitiesSavedHandler = null;
        }
        if (this.uiManager) {
            this.uiManager.destroy();
        }
        this.onUserMessage = null;
        this.initialized = false;
        console.log('[BalancyAI] SDK destroyed');
    }
}
/**
 * Capitalize first letter of string
 */
/** Friendly display names for URL section slugs */
BalancyAISDK.SECTION_DISPLAY_NAMES = {
    'ui-builder': 'UI Builder',
    'scripts-ide': 'Extensions',
    liveops: 'LiveOps',
    templates: 'Templates',
    datastructure: 'Data Structure',
};
// Create and expose global instance
if (typeof window !== 'undefined') {
    window.BalancyAI = new BalancyAISDK();
}
// Also export for module usage
export { BalancyAISDK };
export default BalancyAISDK;
